import React, { useMemo, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Filter } from 'lucide-react';
import {
  type UpcomingPortfolioEvent,
  type UpcomingEventType,
  UPCOMING_EVENT_TYPE_CONFIG,
  CATEGORY_COLORS,
  groupEventsByMonth,
} from '../../../mocks/upcomingEventsMock';

interface PortfolioEventsCalendarProps {
  events: UpcomingPortfolioEvent[];
  onEventClick?: (event: UpcomingPortfolioEvent) => void;
}

type ViewMode = 'timeline' | 'calendar';
type GroupBy = 'site' | 'type' | 'category';

function formatDateShort(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function daysUntil(dateString: string): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(dateString);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function daysUntilLabel(days: number): string {
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${(days / 365).toFixed(1)}yr`;
}

function urgencyColor(days: number): string {
  if (days < 0) return '#EF4444';
  if (days <= 7) return '#EF4444';
  if (days <= 30) return '#F97316';
  if (days <= 90) return '#F59E0B';
  if (days <= 365) return '#3B82F6';
  return '#6B7280';
}

const PortfolioEventsCalendar: React.FC<PortfolioEventsCalendarProps> = ({
  events,
  onEventClick,
}) => {
  const [groupBy, setGroupBy] = useState<GroupBy>('site');
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const filteredEvents = useMemo(() => {
    if (!filterCategory) return events;
    return events.filter((e) => e.category === filterCategory);
  }, [events, filterCategory]);

  // Group events
  const groups = useMemo(() => {
    const map = new Map<string, { label: string; color: string; events: UpcomingPortfolioEvent[] }>();

    filteredEvents.forEach((event) => {
      let key: string;
      let label: string;
      let color: string;

      switch (groupBy) {
        case 'site':
          key = event.site_id;
          label = event.site_name;
          color = '#17c480';
          break;
        case 'type':
          key = event.event_type;
          label = UPCOMING_EVENT_TYPE_CONFIG[event.event_type].label;
          color = UPCOMING_EVENT_TYPE_CONFIG[event.event_type].color;
          break;
        case 'category':
          key = event.category;
          label = event.category.charAt(0).toUpperCase() + event.category.slice(1);
          color = CATEGORY_COLORS[event.category];
          break;
      }

      if (!map.has(key)) map.set(key, { label, color, events: [] });
      map.get(key)!.events.push(event);
    });

    // Sort groups by earliest event
    return Array.from(map.entries())
      .map(([key, group]) => ({
        key,
        ...group,
        events: group.events.sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime()),
        earliestDays: daysUntil(group.events[0]?.due_date || ''),
      }))
      .sort((a, b) => a.earliestDays - b.earliestDays);
  }, [filteredEvents, groupBy]);

  // Auto-expand first 3 groups on initial render
  useMemo(() => {
    if (expandedGroups.size === 0 && groups.length > 0) {
      setExpandedGroups(new Set(groups.slice(0, 3).map((g) => g.key)));
    }
  }, [groups.length]);

  const categories = useMemo(() => {
    const cats = new Set(events.map((e) => e.category));
    return Array.from(cats).sort();
  }, [events]);

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        {/* Group by toggle */}
        <div className="flex items-center gap-0.5 bg-[var(--color-bg-tertiary)] rounded-md p-0.5">
          {(['site', 'type', 'category'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setGroupBy(mode)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                groupBy === mode
                  ? 'bg-[#17c480] text-white'
                  : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              By {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>

        {/* Category filter chips */}
        <div className="flex items-center gap-1.5">
          <Filter className="w-3 h-3" style={{ color: 'var(--color-text-muted)' }} />
          <button
            onClick={() => setFilterCategory(null)}
            className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
              !filterCategory
                ? 'bg-[var(--color-surface-active)] text-[var(--color-text-primary)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilterCategory(filterCategory === cat ? null : cat)}
              className="px-2 py-0.5 rounded text-[11px] font-medium transition-colors"
              style={{
                backgroundColor: filterCategory === cat ? `${CATEGORY_COLORS[cat as keyof typeof CATEGORY_COLORS]}20` : 'transparent',
                color: filterCategory === cat
                  ? CATEGORY_COLORS[cat as keyof typeof CATEGORY_COLORS]
                  : 'var(--color-text-muted)',
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Grouped event list */}
      <div className="space-y-1">
        {groups.map((group) => {
          const isExpanded = expandedGroups.has(group.key);
          const urgentCount = group.events.filter((e) => daysUntil(e.due_date) <= 30).length;

          return (
            <div key={group.key}>
              {/* Group header */}
              <button
                onClick={() => toggleGroup(group.key)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors hover:bg-[var(--color-surface-hover)] text-left"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: group.color }}
                />
                <span className="text-sm font-semibold flex-1" style={{ color: 'var(--color-text-primary)' }}>
                  {group.label}
                </span>
                {urgentCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/15 text-red-500">
                    {urgentCount} urgent
                  </span>
                )}
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {group.events.length}
                </span>
                <ChevronRight
                  className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  style={{ color: 'var(--color-text-muted)' }}
                />
              </button>

              {/* Event rows */}
              {isExpanded && (
                <div className="ml-5 border-l-2 border-[var(--color-border)]">
                  {group.events.map((event) => {
                    const days = daysUntil(event.due_date);
                    const config = UPCOMING_EVENT_TYPE_CONFIG[event.event_type];
                    return (
                      <button
                        key={event.id}
                        onClick={() => onEventClick?.(event)}
                        className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-[var(--color-surface-hover)]"
                      >
                        {/* Timeline dot */}
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0 -ml-[5px]"
                          style={{ backgroundColor: config.color }}
                        />

                        {/* Event info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate" style={{ color: 'var(--color-text-primary)' }}>
                            {event.title}
                          </p>
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                            {formatDateShort(event.due_date)}
                            {groupBy !== 'site' && ` · ${event.site_name}`}
                            {groupBy !== 'type' && ` · ${config.label}`}
                          </p>
                        </div>

                        {/* Urgency badge */}
                        <span
                          className="text-xs font-medium flex-shrink-0 px-1.5 py-0.5 rounded"
                          style={{
                            color: urgencyColor(days),
                            backgroundColor: `${urgencyColor(days)}15`,
                          }}
                        >
                          {daysUntilLabel(days)}
                        </span>

                        {/* Severity */}
                        <span
                          className="w-1.5 h-6 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor:
                              event.severity === 'critical' ? '#EF4444' :
                              event.severity === 'high' ? '#F97316' :
                              event.severity === 'medium' ? '#F59E0B' : '#84CC16',
                          }}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredEvents.length === 0 && (
        <div className="flex items-center justify-center py-8 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          <Calendar className="w-4 h-4 mr-2" />
          No upcoming events
        </div>
      )}
    </div>
  );
};

export default PortfolioEventsCalendar;
